
import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, BookOpen, Euro, Search, Calculator } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { popularLocations, otherLocations, resourceItems, companyItems } from "./navData";

const GUIDE_ITEMS = [
  {
    href: "/guides/finding-nursing-home-ireland-2026",
    icon: Search,
    title: "Finding a Nursing Home 2026",
    description: "6-chapter interactive guide — care needs, HIQA, Fair Deal, and the move.",
  },
  {
    href: "/guides/fair-deal-application-ireland-2026",
    icon: Euro,
    title: "Fair Deal Application Guide 2026",
    description: "Step-by-step walkthrough with documents checklist and financial assessment.",
  },
  {
    href: "/fair-deal",
    icon: Calculator,
    title: "Fair Deal Hub & NTPF Rates",
    description: "Live 2026 NTPF rates, cost calculator, and step-by-step application guide.",
  },
];

interface NavLinksProps {
  locationDropdownOpen: boolean;
  setLocationDropdownOpen: (open: boolean) => void;
  resourcesDropdownOpen: boolean;
  setResourcesDropdownOpen: (open: boolean) => void;
  guidesDropdownOpen?: boolean;
  setGuidesDropdownOpen?: (open: boolean) => void;
}

export default function NavLinks({
  locationDropdownOpen,
  setLocationDropdownOpen,
  resourcesDropdownOpen,
  setResourcesDropdownOpen,
  guidesDropdownOpen = false,
  setGuidesDropdownOpen = () => {},
}: NavLinksProps) {
  return (
    <div className="flex items-center space-x-8">
      {/* Nursing Homes Dropdown */}
      <DropdownMenu open={locationDropdownOpen} onOpenChange={setLocationDropdownOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <button className="group flex items-center space-x-1.5 text-gray-700 hover:text-grandpal-green-dark transition-colors">
            <span className="font-medium">Nursing Homes</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:text-grandpal-green-dark" style={{
              transform: locationDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
            }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-screen p-0 bg-white shadow-md border-none rounded-none border-t border-gray-100 mt-3.5 z-50" align="start" sideOffset={0}>
          <div className="container py-8 animate-fade-in">
            <h3 className="text-grandpal-green-dark mb-6 font-bold text-xl">Popular Nursing Home Locations</h3>
            <div className="grid grid-cols-4 gap-x-16 gap-y-4">
              {popularLocations.map(location => (
                <Link key={location.title} to={location.path} className="text-gray-700 hover:text-grandpal-green-dark transition-colors text-base">
                  {location.title}
                </Link>
              ))}
              {otherLocations.map(location => (
                <Link key={location.title} to={location.path} className="text-gray-700 hover:text-grandpal-green-dark transition-colors text-base">
                  {location.title}
                </Link>
              ))}
            </div>
            <div className="mt-6">
              <Link to="/nursing-homes" className="flex items-center text-grandpal-green-dark font-medium hover:text-grandpal-green-darkHover text-base group">
                View all Nursing Homes
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Guides Dropdown */}
      <DropdownMenu open={guidesDropdownOpen} onOpenChange={setGuidesDropdownOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <button className="group flex items-center space-x-1.5 text-gray-700 hover:text-grandpal-green-dark transition-colors">
            <span className="font-medium">Guides</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:text-grandpal-green-dark" style={{
              transform: guidesDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
            }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[460px] p-0 bg-white shadow-lg border border-gray-100 rounded-xl mt-2 z-50" align="start" sideOffset={4}>
          <div className="p-5 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-grandpal-green-dark font-bold text-base">2026 Interactive Guides</h3>
              <Link to="/guides" className="text-xs text-grandpal-green-dark hover:underline flex items-center gap-1">
                All guides <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <ul className="space-y-2">
              {GUIDE_ITEMS.map((guide) => {
                const Icon = guide.icon;
                return (
                  <li key={guide.href}>
                    <Link
                      to={guide.href}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-grandpal-green-light transition-colors group"
                    >
                      <span className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md bg-[#e5f4ee] group-hover:bg-grandpal-green-lighter transition-colors">
                        <Icon className="h-4 w-4 text-grandpal-green-dark" />
                      </span>
                      <div>
                        <p className="font-medium text-gray-800 text-sm leading-snug">{guide.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-snug">{guide.description}</p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <Link
                to="/guides"
                className="flex items-center gap-1.5 text-sm font-medium text-grandpal-green-dark hover:text-grandpal-green-darkHover transition-colors group"
              >
                <BookOpen className="h-4 w-4" />
                Browse all guides & resources
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Resources Dropdown */}
      <DropdownMenu open={resourcesDropdownOpen} onOpenChange={setResourcesDropdownOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <button className="group flex items-center space-x-1.5 text-gray-700 hover:text-grandpal-green-dark transition-colors">
            <span className="font-medium">Resources</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:text-grandpal-green-dark" style={{
              transform: resourcesDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
            }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-screen p-0 bg-white shadow-md border-none rounded-none border-t border-gray-100 mt-3.5 z-50" align="start" sideOffset={0}>
          <div className="container mx-auto py-10 animate-fade-in">
            <div className="grid grid-cols-2 gap-12">
              <div>
                <h3 className="text-grandpal-green-dark mb-6 text-xl font-bold font-lora">Resources</h3>
                <ul className="flex flex-col gap-2">
                  {resourceItems.map(item => (
                    <li key={item.title}>
                      <Link to={item.path} className="flex items-start gap-4 hover:bg-grandpal-green-light p-3 rounded-md transition-all group">
                        <span className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-md bg-[#e5f4ee] group-hover:bg-grandpal-green-lighter transition-colors">
                          <span className="text-grandpal-green-dark text-2xl">{item.icon}</span>
                        </span>
                        <div>
                          <div className="font-medium text-gray-800 text-lg mb-0.5">{item.title}</div>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-grandpal-green-dark mb-6 text-xl font-bold font-lora">Company</h3>
                <ul className="flex flex-col gap-2">
                  {companyItems.map(item => (
                    <li key={item.title}>
                      <Link to={item.path} className="flex items-start gap-4 hover:bg-grandpal-green-light p-3 rounded-md transition-all group">
                        <span className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-md bg-[#e5f4ee] group-hover:bg-grandpal-green-lighter transition-colors">
                          <span className="text-grandpal-green-dark text-2xl">{item.icon}</span>
                        </span>
                        <div>
                          <div className="font-medium text-gray-800 text-lg mb-0.5">{item.title}</div>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
